import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { adminGetDocuments, adminUpdateDocument } from "@/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const GROUPS = [
  { key: "settings", label: "Site settings", match: (d) => d.doc_id === "site_settings" },
  { key: "services", label: "Service pages", match: (d) => d.doc_id.startsWith("service_") },
  { key: "team", label: "Team", match: (d) => d.doc_id.startsWith("team_") },
  { key: "posts", label: "Blog posts", match: (d) => d.doc_id.startsWith("post_") },
  { key: "magnets", label: "Lead magnets", match: (d) => d.doc_id.startsWith("magnet_") },
];

function Field({ label, value, onChange, textarea, testId, hint }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</label>
      {textarea ? (
        <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={textarea} className="rounded-sm" data-testid={testId} />
      ) : (
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm" data-testid={testId} />
      )}
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ListEditor({ items, onChange, fields, addLabel, emptyItem, testPrefix }) {
  const setItem = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-4">
      {items.map((it, i) => (
        <div key={i} className="space-y-3 border border-border bg-background p-4" data-testid={`${testPrefix}-item-${i}`}>
          <div className="flex justify-end">
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive" aria-label="Remove item" data-testid={`${testPrefix}-remove-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
          {fields.map((f) => (
            <Field key={f.key} label={f.label} value={it[f.key]} onChange={(v) => setItem(i, f.key, f.number ? Number(v) || 0 : v)} textarea={f.textarea} testId={`${testPrefix}-${f.key}-${i}`} />
          ))}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => onChange([...items, { ...emptyItem }])} data-testid={`${testPrefix}-add-btn`}>
        <Plus size={14} className="mr-1" /> {addLabel}
      </Button>
    </div>
  );
}

export default function AdminContent() {
  const [docs, setDocs] = useState([]);
  const [selectedId, setSelectedId] = useState("site_settings");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    adminGetDocuments()
      .then(setDocs)
      .catch(() => toast.error("Could not load content"));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const doc = docs.find((d) => d.doc_id === selectedId);
    setDraft(doc ? JSON.parse(JSON.stringify(doc)) : null);
  }, [selectedId, docs]);

  const grouped = useMemo(() => GROUPS.map((g) => ({ ...g, docs: docs.filter(g.match) })), [docs]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    const { doc_id, ...payload } = draft;
    try {
      await adminUpdateDocument(doc_id, payload);
      toast.success("Saved. Changes are live on the site.");
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const createPost = () => {
    const id = `post_${Date.now().toString(36)}`;
    const newDoc = {
      doc_id: id,
      title: "Untitled post",
      slug: `untitled-${Date.now().toString(36)}`,
      category: "UAE Tax",
      excerpt: "",
      body: "",
      status: "draft",
      author: "TruVerse Middle East",
      created_at: new Date().toISOString(),
    };
    setDocs((ds) => [...ds, newDoc]);
    setSelectedId(id);
  };

  const renderEditor = () => {
    if (!draft) return null;
    if (draft.doc_id === "site_settings")
      return (
        <div className="space-y-6" data-testid="cms-settings-editor">
          <Field label="Positioning line" value={draft.positioning} onChange={(v) => set("positioning", v)} testId="cms-positioning" />
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Email" value={draft.email} onChange={(v) => set("email", v)} testId="cms-email" />
            <Field label="India phone" value={draft.phone_india} onChange={(v) => set("phone_india", v)} testId="cms-phone-india" />
            <Field label="UAE phone" value={draft.phone_uae} onChange={(v) => set("phone_uae", v)} testId="cms-phone-uae" />
            <Field label="WhatsApp number (digits, e.g. 9715XXXXXXX)" value={draft.whatsapp} onChange={(v) => set("whatsapp", v)} testId="cms-whatsapp" hint="Leave empty to hide the floating button." />
          </div>
          <Field label="Dubai address" value={draft.address_dubai} onChange={(v) => set("address_dubai", v)} textarea={3} testId="cms-address-dubai" />
          <Field label="India address" value={draft.address_india} onChange={(v) => set("address_india", v)} textarea={3} testId="cms-address-india" />
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Trust counters</p>
            <ListEditor
              items={draft.counters || []}
              onChange={(v) => set("counters", v)}
              fields={[
                { key: "label", label: "Label" },
                { key: "value", label: "Value", number: true },
                { key: "suffix", label: "Suffix (e.g. +)" },
                { key: "note", label: "Note (shown small, leave empty when confirmed)" },
              ]}
              addLabel="Add counter"
              emptyItem={{ id: `c${Date.now()}`, label: "", value: 0, suffix: "", note: "" }}
              testPrefix="cms-counter"
            />
          </div>
        </div>
      );
    if (draft.doc_id.startsWith("service_"))
      return (
        <div className="space-y-6" data-testid="cms-service-editor">
          <Field label="Title" value={draft.title} onChange={(v) => set("title", v)} testId="cms-service-title" />
          <Field label="Meta description (SEO)" value={draft.meta_description} onChange={(v) => set("meta_description", v)} textarea={2} testId="cms-service-meta" />
          <Field label="Intro" value={draft.intro} onChange={(v) => set("intro", v)} textarea={4} testId="cms-service-intro" />
          <Field label="CTA label" value={draft.cta_label} onChange={(v) => set("cta_label", v)} testId="cms-service-cta" />
          {Array.isArray(draft.key_facts) && draft.key_facts.length > 0 && (
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Key facts</p>
              <ListEditor
                items={draft.key_facts}
                onChange={(v) => set("key_facts", v)}
                fields={[
                  { key: "label", label: "Label" },
                  { key: "value", label: "Value" },
                  { key: "note", label: "Note" },
                ]}
                addLabel="Add fact"
                emptyItem={{ label: "", value: "", note: "" }}
                testPrefix="cms-fact"
              />
            </div>
          )}
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">FAQs</p>
            <ListEditor
              items={draft.faqs || []}
              onChange={(v) => set("faqs", v)}
              fields={[
                { key: "q", label: "Question" },
                { key: "a", label: "Answer", textarea: 3 },
              ]}
              addLabel="Add FAQ"
              emptyItem={{ q: "", a: "" }}
              testPrefix="cms-faq"
            />
          </div>
        </div>
      );
    if (draft.doc_id.startsWith("team_"))
      return (
        <div className="space-y-6" data-testid="cms-team-editor">
          <Field label="Name" value={draft.name} onChange={(v) => set("name", v)} testId="cms-team-name" />
          <Field label="Role" value={draft.role} onChange={(v) => set("role", v)} testId="cms-team-role" />
          <Field label="Bio" value={draft.bio} onChange={(v) => set("bio", v)} textarea={4} testId="cms-team-bio" />
          <Field label="LinkedIn URL" value={draft.linkedin} onChange={(v) => set("linkedin", v)} testId="cms-team-linkedin" />
        </div>
      );
    if (draft.doc_id.startsWith("post_"))
      return (
        <div className="space-y-6" data-testid="cms-post-editor">
          <Field label="Title" value={draft.title} onChange={(v) => set("title", v)} testId="cms-post-title" />
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Slug (URL)" value={draft.slug} onChange={(v) => set("slug", v)} testId="cms-post-slug" />
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Category</label>
              <Select value={draft.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="rounded-sm" data-testid="cms-post-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["UAE Tax", "Transfer Pricing", "GCC", "Family Office", "India UAE"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Field label="Excerpt" value={draft.excerpt} onChange={(v) => set("excerpt", v)} textarea={2} testId="cms-post-excerpt" />
          <Field label="Body (blank line between paragraphs)" value={draft.body} onChange={(v) => set("body", v)} textarea={12} testId="cms-post-body" />
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Status</label>
            <Select value={draft.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="w-48 rounded-sm" data-testid="cms-post-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    if (draft.doc_id.startsWith("magnet_"))
      return (
        <div className="space-y-6" data-testid="cms-magnet-editor">
          <Field label="Title" value={draft.title} onChange={(v) => set("title", v)} testId="cms-magnet-title" />
          <Field label="Description" value={draft.description} onChange={(v) => set("description", v)} textarea={3} testId="cms-magnet-description" />
          <Field label="File URL (leave empty to use the placeholder PDF)" value={draft.file_url} onChange={(v) => set("file_url", v)} testId="cms-magnet-file-url" />
        </div>
      );
    return null;
  };

  return (
    <div data-testid="admin-content-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl text-foreground">CMS content</h1>
          <p className="mt-2 text-sm text-muted-foreground">Edit counters, contact details, service text, FAQs, team and posts. Changes go live immediately.</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={createPost} data-testid="cms-new-post-btn">
          <Plus size={14} className="mr-1" /> New post
        </Button>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-4">
        <aside className="space-y-6" data-testid="cms-doc-list">
          {grouped.map((g) => (
            <div key={g.key}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{g.label}</p>
              <div className="mt-2 space-y-1">
                {g.docs.map((d) => (
                  <button
                    key={d.doc_id}
                    type="button"
                    onClick={() => setSelectedId(d.doc_id)}
                    data-testid={`cms-doc-${d.doc_id}`}
                    className={`block w-full rounded-sm px-3 py-2 text-left text-sm transition-colors duration-150 ${
                      selectedId === d.doc_id ? "bg-accent/15 text-accent" : "text-foreground hover:bg-secondary"
                    }`}
                  >
                    {d.title || d.name || d.brand_name || d.doc_id}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <div className="lg:col-span-3">
          <div className="border border-border bg-card p-8">
            {renderEditor()}
            <div className="mt-8 border-t border-border pt-6">
              <Button onClick={save} disabled={saving || !draft} data-testid="cms-save-btn" className="rounded-sm bg-accent px-8 text-sm font-semibold text-accent-foreground hover:bg-accent/85">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
